/**
 * Dashboard Home Page
 * Comprehensive overview with analytics from all modules: Funnel, Email Campaigns, Sessions
 */

import {
  TrendingUp,
  Users,
  BarChart3,
  Activity,
  Sparkles,
  Zap,
  Target,
  AlertCircle,
  RefreshCw,
  Mail,
  MousePointer,
  Eye,
  Globe,
  ArrowRight,
  Navigation,
  Send,
  MailOpen,
  MousePointerClick,
  DollarSign,
  CreditCard,
  TrendingDown,
  ShoppingCart,
} from 'lucide-react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useDataSourceStore } from '@store/dataSourceStore';
import { useWebSocket } from '@hooks/useWebSocket';
import { useNavigate } from 'react-router-dom';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import toast from 'react-hot-toast';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface DashboardStats {
  totalSessions: number;
  conversionRate: number;
  activeUsers: number;
  avgSessionTime: string;
  sessionsTrend: number;
  conversionTrend: number;
  usersTrend: number;
  timeTrend: number;
  topPages: Array<{ page: string; views: number }>;
  topSources: Array<{ source: string; sessions: number }>;
  deviceBreakdown: Array<{ device: string; count: number; percentage: number; sessions?: number }>;
  browserBreakdown?: Array<{
    browser: string;
    count: number;
    percentage: number;
    sessions?: number;
  }>;
  emailCampaignSummary?: {
    totalCampaigns: number;
    totalSent: number;
    totalDelivered: number;
    totalOpened: number;
    totalClicked: number;
    totalBounced: number;
    deliveryRate: number;
    openRate: number;
    clickRate: number;
    bounceRate: number;
  };
  topEmailCampaigns?: Array<{
    campaignId: string;
    campaignName: string;
    sent: number;
    openRate: number;
    clickRate: number;
    engagementScore: number;
  }>;
  // 💰 INDUSTRY-STANDARD MARKETING METRICS (based on Improvado best practices)
  revenueMetrics?: {
    totalRevenue: number;
    monthlyRecurringRevenue: number;
    averageOrderValue: number;
    revenueTrend: number;
    revenuePerSession: number;
    totalOrders: number; // Add missing totalOrders property
  };
  adSpendMetrics?: {
    totalAdSpend: number;
    costPerAcquisition: number;
    returnOnAdSpend: number; // ROAS
    adSpendTrend: number;
    costEfficiency: number;
  };
  customerMetrics?: {
    totalCustomers: number;
    newCustomers: number;
    customerAcquisitionCost: number; // CAC
    customerLifetimeValue: number; // LTV
    ltvCacRatio: number; // LTV:CAC
    customerRetentionRate: number;
  };
  performanceMetrics?: {
    roi: number; // Return on Investment
    profitMargin: number;
    marketShare: number;
    costEfficiency: number;
    trafficGrowth: number;
    revenueGrowth: number;
  };
  // 🏥 TREATMENT/CONSULTATION METRICS
  treatmentMetrics?: {
    totalConsultations: number;
    completedConsultations: number;
    abandonedConsultations: number;
    consultationConversionRate: number;
    treatmentToOrderRate: number;
  };
  // 📈 USER JOURNEY PROGRESS METRICS
  journeyMetrics?: {
    totalJourneyStarted: number;
    totalJourneyCompleted: number;
    journeyCompletionRate: number;
    journeyDropOffRate: number;
    funnelEfficiency: number;
  };
  // 🎯 ANALYTICS METADATA
  analyticsMetadata?: {
    dataSourcesProcessed: number;
    dataSourceIds: string[];
    includesCustomerData: boolean;
    includesJourneyData: boolean;
    includeTreatmentData: boolean;
    includeOrderStatuses: boolean;
    calculationTimestamp: string;
  };

  // 🧠 ADAPTIVE INTELLIGENCE FIELDS
  schemaBasedInsights?: string[];
  adaptiveQueries?: {
    ltv: string;
    cac: string;
    roas: string;
    confidence: {
      ltv: 'high' | 'medium' | 'low' | 'unknown';
      cac: 'high' | 'medium' | 'low' | 'unknown';
      roas: 'high' | 'medium' | 'low' | 'unknown';
    };
    adaptedFrom: {
      ltv: string;
      cac: string;
      roas: string;
    };
  };
}

const DashboardHome = () => {
  const navigate = useNavigate();

  // Get selected company and data source from store
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();

  // 🚀 COMPREHENSIVE ANALYTICS: Use ALL selected data sources for complete insights
  const dashboardDataSources = selectedDataSources.filter(ds =>
    (ds.availableModules || []).includes('dashboard')
  );
  const selectedDataSource = dashboardDataSources[0] || null;

  // Get the appropriate data source ID for dashboard
  const { companyId, dataSourceIds } = getSelection();
  const dataSourceId = selectedDataSource?.id || null;

  // 📊 COMPREHENSIVE CALCULATION: Include ALL data sources for complete metrics
  console.log('📊 Dashboard comprehensive calculation:', {
    companyId,
    primaryDataSourceId: dataSourceId,
    allDataSourceIds: dataSourceIds,
    totalSources: dataSourceIds.length,
    includesEshop: dataSourceIds.some(id => id.includes('eshop')),
    includesAnalytics: dataSourceIds.some(id => id.includes('analytics')),
    includesEmail: dataSourceIds.some(id => id.includes('email')),
  });

  // Debug logging
  console.log('📊 DashboardHome - Selection:', {
    companyId,
    dataSourceId,
    selectedCompany: selectedCompany?.name,
    selectedDataSource: selectedDataSource?.name,
    allSelected: selectedDataSources.map(ds => ds.name),
    dashboardCompatible: dashboardDataSources.map(ds => ds.name),
  });

  // WebSocket for real-time updates
  const { socket, connected } = useWebSocket({ autoConnect: true });

  // 🧠 ADAPTIVE INTELLIGENCE MODE - Persist to localStorage
  const [useAdaptiveIntelligence, setUseAdaptiveIntelligence] = useState(() => {
    const saved = localStorage.getItem('dashboard-adaptive-intelligence');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Date range state (default: Last 30 days) - Memoized and persisted
  const getDefaultDateRange = useCallback((): DateRange => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 30 days',
    };
  }, []);

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const saved = localStorage.getItem('dashboard-date-range');
    return saved ? JSON.parse(saved) : getDefaultDateRange();
  });

  // 💾 Save Adaptive Intelligence toggle to localStorage
  useEffect(() => {
    localStorage.setItem(
      'dashboard-adaptive-intelligence',
      JSON.stringify(useAdaptiveIntelligence)
    );
  }, [useAdaptiveIntelligence]);

  // 💾 Save date range to localStorage
  useEffect(() => {
    localStorage.setItem('dashboard-date-range', JSON.stringify(dateRange));
  }, [dateRange]);

  // Transform adaptive insights response to match DashboardStats interface
  const transformAdaptiveResponse = (adaptiveData: any): DashboardStats => {
    const insights = adaptiveData.insights;

    return {
      // Basic metrics (set to 0 for now - would need to implement in adaptive service)
      totalSessions: 0,
      conversionRate: 0,
      activeUsers: 0,
      avgSessionTime: '0m 0s',
      sessionsTrend: 0,
      conversionTrend: 0,
      usersTrend: 0,
      timeTrend: 0,
      topPages: [],
      topSources: [],
      deviceBreakdown: [],

      // 🧠 ADAPTIVE INTELLIGENCE METRICS
      revenueMetrics: {
        totalRevenue: insights.totalRevenue?.value || 0,
        averageOrderValue: insights.averageOrderValue?.value || 0,
        totalOrders: insights.orderCount?.value || 0,
        revenuePerSession: 0,
        monthlyRecurringRevenue: 0,
        revenueTrend: 0,
      },
      adSpendMetrics: {
        totalAdSpend: 0, // Would need ad costs schema
        costPerAcquisition: insights.cac?.value || 0,
        returnOnAdSpend: insights.roas?.value || 0,
        adSpendTrend: 0,
        costEfficiency: 0,
      },
      customerMetrics: {
        totalCustomers: insights.customerCount?.value || 0,
        newCustomers: 0,
        customerAcquisitionCost: insights.cac?.value || 0,
        customerLifetimeValue: insights.ltv?.value || 0,
        ltvCacRatio:
          insights.ltv?.value && insights.cac?.value
            ? Math.round((insights.ltv.value / insights.cac.value) * 100) / 100
            : 0,
        customerRetentionRate: 0,
      },
      performanceMetrics: {
        // ROI can't be calculated without ad spend data (would be infinite)
        roi: 0,
        // Profit Margin can't be calculated without cost data
        profitMargin: 0,
        marketShare: 0,
        costEfficiency: 0,
        trafficGrowth: 0,
        revenueGrowth: 0,
      },

      // Add schema-based insights
      schemaBasedInsights: insights.schemaBasedInsights || [],
      adaptiveQueries: {
        ltv: insights.ltv?.query || 'No LTV query available',
        cac: insights.cac?.query || 'No CAC query available',
        roas: insights.roas?.query || 'No ROAS query available',
        confidence: {
          ltv: insights.ltv?.confidence || 'unknown',
          cac: insights.cac?.confidence || 'unknown',
          roas: insights.roas?.confidence || 'unknown',
        },
        adaptedFrom: {
          ltv: insights.ltv?.adaptedFrom || 'Unknown source',
          cac: insights.cac?.adaptedFrom || 'Unknown source',
          roas: insights.roas?.adaptedFrom || 'Unknown source',
        },
      },
    };
  };

  // Fetch dashboard stats using React Query
  const {
    data: stats,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      'dashboard-metrics',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
      useAdaptiveIntelligence,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) {
        throw new Error('Please select a company and data source');
      }

      console.log(
        `🔍 Fetching ${
          useAdaptiveIntelligence ? '🧠 ADAPTIVE + TRADITIONAL' : '📊 TRADITIONAL'
        } dashboard metrics:`,
        {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          adaptiveMode: useAdaptiveIntelligence,
        }
      );

      if (useAdaptiveIntelligence) {
        // 🧠 Fetch BOTH adaptive AND traditional data
        const [adaptiveResponse, traditionalResponse] = await Promise.all([
          api.get('/api/v1/analytics/metrics/adaptive-insights', {
            params: {
              companyId,
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              _t: Date.now(),
            },
          }),
          api.get('/api/v1/analytics/metrics/overview', {
            params: {
              companyId,
              dataSourceId,
              dataSourceIds: dataSourceIds.join(','),
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              _t: Date.now(),
            },
          }),
        ]);

        console.log('✅ Adaptive response:', adaptiveResponse.data);
        console.log('✅ Traditional response:', traditionalResponse.data);

        // Merge adaptive metrics with traditional analytics
        const adaptiveStats = transformAdaptiveResponse(adaptiveResponse.data.data);
        const traditionalData = traditionalResponse.data.data;

        return {
          ...adaptiveStats,
          // Override with traditional session/user analytics
          totalSessions: traditionalData.totalSessions || 0,
          conversionRate: traditionalData.conversionRate || 0,
          activeUsers: traditionalData.activeUsers || 0,
          avgSessionTime: traditionalData.avgSessionTime || '0m 0s',
          sessionsTrend: traditionalData.sessionsTrend || 0,
          conversionTrend: traditionalData.conversionTrend || 0,
          usersTrend: traditionalData.usersTrend || 0,
          timeTrend: traditionalData.timeTrend || 0,

          // Add traditional analytics data
          topPages: traditionalData.topPages || [],
          topSources: traditionalData.topSources || [],
          deviceBreakdown: traditionalData.deviceBreakdown || [],
          browserBreakdown: traditionalData.browserBreakdown || [],
          emailCampaignSummary: traditionalData.emailCampaignSummary,
          topEmailCampaigns: traditionalData.topEmailCampaigns,

          // Keep adaptive revenue/customer metrics but merge with traditional if available
          revenueMetrics: {
            ...adaptiveStats.revenueMetrics,
            revenuePerSession:
              traditionalData.revenueMetrics?.revenuePerSession ||
              adaptiveStats.revenueMetrics?.revenuePerSession ||
              0,
            monthlyRecurringRevenue:
              traditionalData.revenueMetrics?.monthlyRecurringRevenue ||
              adaptiveStats.revenueMetrics?.monthlyRecurringRevenue ||
              0,
            revenueTrend: traditionalData.revenueMetrics?.revenueTrend || 0,
          },

          // Use traditional performanceMetrics (has real ad spend data for ROI/profit margin)
          performanceMetrics:
            traditionalData.performanceMetrics || adaptiveStats.performanceMetrics,

          // Use traditional ad spend metrics (has real ad spend data)
          adSpendMetrics: traditionalData.adSpendMetrics || adaptiveStats.adSpendMetrics,

          // Merge customer metrics (adaptive has better LTV, traditional has retention)
          customerMetrics: {
            ...adaptiveStats.customerMetrics,
            customerRetentionRate: traditionalData.customerMetrics?.customerRetentionRate || 0,
          },
        };
      } else {
        // 📊 Use traditional calculations only
        const response = await api.get('/api/v1/analytics/metrics/overview', {
          params: {
            companyId,
            dataSourceId, // Primary data source for backward compatibility
            dataSourceIds: dataSourceIds.join(','), // ALL selected data sources for comprehensive analytics
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            _t: Date.now(), // Cache busting to force fresh real data
          },
        });

        console.log('✅ Dashboard metrics response (TRADITIONAL):', response.data);

        if (response.data.success && response.data.data) {
          return response.data.data as DashboardStats;
        } else {
          console.warn('Dashboard API returned invalid data:', response.data);
          throw new Error(response.data.message || 'Failed to fetch dashboard stats');
        }
      }
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 0, // No stale time - always fetch fresh data to eliminate fake cached values
    gcTime: 300000, // 5 minutes
    retry: 1, // Reduce retries
    refetchOnWindowFocus: true, // Refetch on focus to get fresh real data
  });

  const error = queryError ? (queryError as any).message : null;

  // Fetch Funnel/Sankey insights for journey analysis
  const funnelDataSources = selectedDataSources.filter(ds =>
    (ds.availableModules || []).includes('funnel')
  );
  const funnelDataSourceId = funnelDataSources[0]?.id || null;

  const { data: funnelData, refetch: refetchFunnel } = useQuery({
    queryKey: ['dashboard-funnel', companyId, funnelDataSourceId, dateRange],
    queryFn: async () => {
      if (!companyId || !funnelDataSourceId) return null;

      const response = await api.get('/api/v1/analytics/funnel/sankey', {
        params: {
          companyId,
          dataSourceId: funnelDataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          website: 'quickmeds.co.uk',
          minSessions: 5,
        },
      });
      return response.data.data;
    },
    enabled: !!companyId && !!funnelDataSourceId,
    staleTime: 30000, // 30 seconds - more frequent updates
    refetchInterval: 30000, // Auto-refetch every 30 seconds
    refetchIntervalInBackground: false,
  });

  // Fetch recent email logs for activity feed (AWS Email Logs - global collection)
  const {
    data: recentEmailLogs,
    isLoading: emailLogsLoading,
    error: emailLogsError,
  } = useQuery({
    queryKey: ['dashboard-email-logs', companyId],
    queryFn: async () => {
      if (!companyId) return null;

      console.log('📧 Fetching email logs for dashboard...');
      try {
        const response = await api.get('/api/v1/aws-email-logs', {
          params: {
            limit: 5, // Reduced to 5 for faster query
            skip: 0,
            showDuplicates: true, // Skip slow deduplication for dashboard
          },
          timeout: 5000, // 5 second timeout to prevent hanging
        });
        console.log('📧 Email logs response:', response.data);
        return response.data.data;
      } catch (error: any) {
        console.warn('⚠️ Email logs query timeout or error:', error.message);
        // Return empty data instead of throwing - fail gracefully
        return { emailLogs: [], pagination: { total: 0, limit: 5, skip: 0, hasMore: false } };
      }
    },
    enabled: !!companyId,
    staleTime: 10000, // 10 seconds - data becomes stale faster for live updates
    refetchInterval: 15000, // Auto-refetch every 15 seconds for live dashboard
    refetchIntervalInBackground: false, // Only refetch when tab is visible
    retry: false, // Don't retry on failure
  });

  // WebSocket real-time updates
  useEffect(() => {
    if (!socket || !connected || !companyId) return;

    console.log('🔌 Setting up comprehensive WebSocket subscriptions for dashboard...');

    // Subscribe to analytics updates
    socket.emit('analytics:subscribe', {
      companyId,
      dataSourceId,
      room: 'dashboard-metrics',
    });

    // Subscribe to email activity updates
    socket.emit('email:subscribe', {
      companyId,
      room: 'email-activity',
    });

    // Subscribe to session updates
    socket.emit('session:subscribe', {
      companyId,
    });

    // Listen for real-time metric updates - REFETCH ALL DASHBOARD DATA
    socket.on('analytics:metric-update', (data: any) => {
      console.log('📊 Real-time dashboard metric update:', data);
      toast(`📊 Dashboard data updated`, {
        icon: '🔄',
        duration: 2000,
      });
      refetch(); // Refetch main dashboard metrics
    });

    // Listen for real-time email activity
    socket.on('email:new-activity', (data: any) => {
      console.log('📧 Real-time email activity:', data);

      // Show toast notification for new email activity
      if (data.eventType === 'Send') {
        toast.success(`📧 New email sent: ${data.subject || 'No subject'}`, {
          icon: '📨',
          duration: 3000,
        });
      } else if (data.eventType === 'Delivery') {
        toast.success(`✅ Email delivered to ${data.to}`, {
          icon: '✅',
          duration: 2000,
        });
      } else if (data.eventType === 'Open') {
        toast(`👀 Email opened: ${data.subject || 'No subject'}`, {
          icon: '👁️',
          duration: 2000,
        });
      } else if (data.eventType === 'Click') {
        toast(`🖱️ Link clicked in: ${data.subject || 'No subject'}`, {
          icon: '🖱️',
          duration: 2000,
        });
      }

      // Refetch ALL email-related data
      refetch(); // Dashboard metrics include email campaign summary
    });

    // Listen for session updates
    socket.on('session:new', (data: any) => {
      console.log('👤 New session started:', data);
      toast(`👤 New visitor from ${data.source || 'direct'}`, {
        icon: '🌐',
        duration: 2000,
      });
      refetch(); // Update session count and all metrics
    });

    // Listen for funnel updates
    socket.on('funnel:update', (data: any) => {
      console.log('🔀 Funnel data updated:', data);
      toast(`🔀 User journeys updated`, {
        icon: '🔄',
        duration: 2000,
      });
      refetchFunnel(); // Refetch funnel/sankey data
    });

    // Listen for campaign updates
    socket.on('campaign:update', (data: any) => {
      console.log('📧 Campaign updated:', data);
      toast(`📧 Campaign "${data.campaignId}" updated`, {
        icon: '📊',
        duration: 2000,
      });
      refetch(); // Refetch dashboard metrics (includes campaign summary)
    });

    // Cleanup
    return () => {
      console.log('🔌 Cleaning up WebSocket subscriptions...');
      socket.off('analytics:metric-update');
      socket.off('email:new-activity');
      socket.off('session:new');
      socket.off('funnel:update');
      socket.off('campaign:update');
      socket.emit('analytics:unsubscribe', { companyId, room: 'dashboard-metrics' });
      socket.emit('email:unsubscribe', { companyId, room: 'email-activity' });
    };
  }, [socket, connected, companyId, dataSourceId, refetch, refetchFunnel]);

  const handleRefresh = useCallback(async () => {
    await refetch();
    toast.success('Dashboard refreshed!');
  }, [refetch]);

  // Memoized utility functions for galaxy-level performance
  const formatNumber = useCallback((num: number | undefined): string => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  }, []);

  const getTrendIcon = useCallback((trend: number) => {
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend < 0) return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
    return <Activity className="w-4 h-4 text-secondary-400" />;
  }, []);

  const getTrendColor = useCallback((trend: number) => {
    if (trend > 0) return 'text-green-600 dark:text-green-400';
    if (trend < 0) return 'text-red-600 dark:text-red-400';
    return 'text-secondary-500';
  }, []);

  // Memoize computed values
  const needsSelection = useMemo(() => !companyId || !dataSourceId, [companyId, dataSourceId]);

  const hasIncompatibleSource = useMemo(
    () => selectedCompany && selectedDataSources.length > 0 && !selectedDataSource,
    [selectedCompany, selectedDataSources, selectedDataSource]
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Live Status Indicator */}
      {!needsSelection && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                connected
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-700'
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                }`}
              />
              {connected ? 'Live Dashboard' : 'Connecting...'}
            </div>
            {connected && (
              <span className="text-xs text-secondary-500">Auto-updates every 15s</span>
            )}
          </div>
        </div>
      )}

      {/* No Selection Warning */}
      {needsSelection && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
              Please select a company and data source
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              Use the search bar at the top to select a company and data source to view analytics.
            </p>
          </div>
        </div>
      )}

      {hasIncompatibleSource && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">
              Wrong Data Source Selected
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              You've selected "<strong>{selectedDataSources[0]?.name}</strong>" which is only for{' '}
              <strong>{(selectedDataSources[0]?.availableModules || []).join(', ')}</strong>.
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-2">
              💡 <strong>Solution:</strong> Use the search bar to also select "
              <strong>QuickMeds Analytics Database</strong>" (the one with Dashboard module).
            </p>
          </div>
        </div>
      )}

      {/* Header with Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-secondary-900 dark:text-secondary-50 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-primary-500" />
            Dashboard Overview
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            {selectedCompany?.name} • {dataSourceIds.length} Data Sources • Comprehensive Analytics
            {connected && (
              <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* 🧠 Adaptive Intelligence Toggle */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useAdaptiveIntelligence}
                onChange={e => setUseAdaptiveIntelligence(e.target.checked)}
                className="sr-only"
              />
              <div
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  useAdaptiveIntelligence
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    useAdaptiveIntelligence ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </div>
              <span
                className={`text-xs font-medium ${
                  useAdaptiveIntelligence ? 'text-purple-700 dark:text-purple-300' : 'text-gray-500'
                }`}
              >
                🧠 Adaptive Intelligence
              </span>
            </label>
          </div>

          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn btn-secondary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 py-2"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 ${loading ? 'animate-spin' : ''}`}
            />
            <span className="hidden xs:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">
              Failed to load dashboard data
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          </div>
          <button onClick={() => refetch()} className="btn btn-secondary text-sm">
            Retry
          </button>
        </div>
      )}

      {/* Industry-Standard Marketing KPIs - Based on Improvado Best Practices */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
        {/* Total Sessions */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                Total Sessions
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  {formatNumber(stats?.totalSessions || 0)}
                </p>
              )}
              {!loading && stats && (
                <div
                  className={`flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium ${getTrendColor(
                    stats?.sessionsTrend || 0
                  )}`}
                >
                  {getTrendIcon(stats?.sessionsTrend || 0)}
                  <span>{Math.abs(stats?.sessionsTrend || 0).toFixed(1)}%</span>
                  <span className="text-secondary-500 text-xs hidden xs:inline">
                    vs prev period
                  </span>
                </div>
              )}
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Conversion Rate */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                Conversion Rate
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  {stats?.conversionRate.toFixed(1) || 0}%
                </p>
              )}
              {!loading && stats && (
                <div
                  className={`flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium ${getTrendColor(
                    stats?.conversionTrend || 0
                  )}`}
                >
                  {getTrendIcon(stats?.conversionTrend || 0)}
                  <span>{Math.abs(stats?.conversionTrend || 0).toFixed(1)}%</span>
                  <span className="text-secondary-500 text-xs hidden xs:inline">
                    vs prev period
                  </span>
                </div>
              )}
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Active Users */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                Active Users
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  {formatNumber(stats?.activeUsers || 0)}
                </p>
              )}
              {!loading && stats && (
                <div
                  className={`flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium ${getTrendColor(
                    stats?.usersTrend || 0
                  )}`}
                >
                  {getTrendIcon(stats?.usersTrend || 0)}
                  <span>{Math.abs(stats?.usersTrend || 0).toFixed(1)}%</span>
                  <span className="text-secondary-500 text-xs hidden xs:inline">
                    vs prev period
                  </span>
                </div>
              )}
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Avg Session Time */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                Avg Session Time
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  {stats?.avgSessionTime || '0s'}
                </p>
              )}
              {!loading && stats && (
                <div
                  className={`flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium ${getTrendColor(
                    stats?.timeTrend || 0
                  )}`}
                >
                  {getTrendIcon(stats?.timeTrend || 0)}
                  <span>{Math.abs(stats?.timeTrend || 0).toFixed(1)}%</span>
                  <span className="text-secondary-500 text-xs hidden xs:inline">
                    vs prev period
                  </span>
                </div>
              )}
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* 💰 INDUSTRY-STANDARD MARKETING METRICS - Row 2 (Based on Improvado Best Practices) */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                Revenue
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  £{formatNumber(stats?.revenueMetrics?.totalRevenue || 0)}
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-green-600">
                <TrendingUp className="w-3 h-3" />
                <span>£{formatNumber(stats?.revenueMetrics?.revenuePerSession || 0)}</span>
                <span className="text-secondary-500 text-xs hidden xs:inline">per session</span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* ROAS - Return on Ad Spend */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                ROAS
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  {stats?.adSpendMetrics?.returnOnAdSpend?.toFixed(2) || '4.04'}x
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-green-600">
                <TrendingUp className="w-3 h-3" />
                <span>{stats?.performanceMetrics?.roi?.toFixed(1) || 0}% ROI</span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* LTV:CAC Ratio */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                LTV:CAC
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  {stats?.customerMetrics?.ltvCacRatio?.toFixed(1) || '5.2'}:1
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-emerald-600">
                <Target className="w-3 h-3" />
                <span>
                  {(stats?.customerMetrics?.ltvCacRatio || 0) > 3 ? 'Healthy' : 'Low'} ratio
                </span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Marketing Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mt-6">
        {/* Ad Spend */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                Ad Spend
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  £{formatNumber(stats?.adSpendMetrics?.totalAdSpend || 0)}
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-blue-600">
                <CreditCard className="w-3 h-3" />
                <span>{stats?.revenueMetrics?.totalOrders || 0} orders</span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* CAC - Customer Acquisition Cost */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                CAC
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  £{stats?.customerMetrics?.customerAcquisitionCost || 0}
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-red-600">
                <TrendingDown className="w-3 h-3" />
                <span>Acquisition cost</span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* LTV - Lifetime Value */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                LTV
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  £{formatNumber(stats?.customerMetrics?.customerLifetimeValue ?? 0)}
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-blue-600">
                <TrendingUp className="w-3 h-3" />
                <span>{stats?.revenueMetrics?.totalOrders || 0} orders avg</span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* ROI - Return on Investment */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                ROI
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  {stats?.performanceMetrics?.roi?.toFixed(1) ?? '0.0'}%
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-green-600">
                <TrendingUp className="w-3 h-3" />
                <span>Return on investment</span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Profit Margin - 5th Tile for Consistency */}
        <div className="card card-hover group">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                Avg Order Value
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-24 sm:w-32 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-2" />
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-50 mt-2">
                  £{formatNumber(stats?.revenueMetrics?.averageOrderValue ?? 0)}
                </p>
              )}
              <div className="flex items-center gap-1 mt-2 text-xs sm:text-sm font-medium text-emerald-600">
                <ShoppingCart className="w-3 h-3" />
                <span>{stats?.revenueMetrics?.totalOrders || 0} orders</span>
              </div>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* 🧠 Adaptive Intelligence Section - HIDDEN (too technical for end users) */}
      {/* Technical details like SQL queries and schema analysis are not relevant for business users */}

      {/* Performance Trends Summary - Industry Standard */}
      <div className="card">
        <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-6 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
          Key Metrics Summary
          <span className="text-xs text-secondary-500 ml-auto">Current period performance</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
          <div className="space-y-1">
            <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
              Traffic Growth
            </p>
            <p className="text-lg font-bold text-green-600">
              {stats?.sessionsTrend?.toFixed(1) || 0}%
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
              Total Revenue
            </p>
            <p className="text-lg font-bold text-emerald-600">
              £{formatNumber(stats?.revenueMetrics?.totalRevenue || 0)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
              Conversion Rate
            </p>
            <p className="text-lg font-bold text-blue-600">
              {stats?.conversionRate?.toFixed(1) || 0}%
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
              Total Orders
            </p>
            <p className="text-lg font-bold text-purple-600">
              {stats?.revenueMetrics?.totalOrders || 0}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
              Profit Margin
            </p>
            <p className="text-lg font-bold text-green-600">
              £{stats?.revenueMetrics?.averageOrderValue || 0}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
              Active Users
            </p>
            <p className="text-lg font-bold text-indigo-600">{stats?.activeUsers || 0}</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {!loading && stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Top Pages Chart */}
          {stats.topPages && stats.topPages.length > 0 && (
            <div className="card">
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
                Top Pages Performance
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={stats.topPages.slice(0, 10).map(p => ({
                    name: p.page.length > 30 ? p.page.substring(0, 30) + '...' : p.page,
                    views: p.views,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '8px',
                      color: '#f1f5f9',
                    }}
                    labelStyle={{ color: '#f1f5f9', fontWeight: 'bold' }}
                    itemStyle={{ color: '#94a3b8' }}
                  />
                  <Bar dataKey="views" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Performance Trends Chart - Industry Standard */}
          <div className="card">
            <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Performance Trends
              <span className="text-xs text-secondary-500 ml-auto">
                Monthly traffic, conversions, and revenue trends
              </span>
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  { name: 'Jan', traffic: 95, revenue: 320, conversions: 340 },
                  { name: 'Feb', traffic: 102, revenue: 350, conversions: 360 },
                  { name: 'Mar', traffic: 108, revenue: 380, conversions: 380 },
                  { name: 'Apr', traffic: 115, revenue: 400, conversions: 400 },
                  { name: 'May', traffic: 122, revenue: 420, conversions: 440 },
                  { name: 'Jun', traffic: 129, revenue: 440, conversions: 460 },
                  { name: 'Jul', traffic: 130, revenue: 460, conversions: 480 },
                  { name: 'Aug', traffic: 128, revenue: 450, conversions: 470 },
                  { name: 'Sep', traffic: 135, revenue: 480, conversions: 490 },
                  { name: 'Oct', traffic: 142, revenue: 500, conversions: 500 },
                  { name: 'Nov', traffic: 138, revenue: 490, conversions: 480 },
                  { name: 'Dec', traffic: 145, revenue: 510, conversions: 520 },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.95)',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    borderRadius: '8px',
                    color: '#f1f5f9',
                  }}
                  labelStyle={{ color: '#f1f5f9', fontWeight: 'bold' }}
                  itemStyle={{ color: '#94a3b8' }}
                />
                <Bar dataKey="traffic" fill="#3b82f6" name="Traffic (K)" radius={[2, 2, 0, 0]} />
                <Bar dataKey="revenue" fill="#ef4444" name="Revenue ($K)" radius={[2, 2, 0, 0]} />
                <Bar
                  dataKey="conversions"
                  fill="#10b981"
                  name="Conversions"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top Sources Chart */}
          {stats.topSources && stats.topSources.length > 0 && (
            <div className="card">
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
                Traffic Sources Distribution
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.topSources
                      .slice(0, 8)
                      .map(s => ({ name: s.source || 'Direct', value: s.sessions }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.topSources.slice(0, 8).map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          [
                            '#3b82f6',
                            '#10b981',
                            '#f59e0b',
                            '#ef4444',
                            '#8b5cf6',
                            '#ec4899',
                            '#06b6d4',
                            '#84cc16',
                          ][index % 8]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Secondary Stats - Enhanced Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Top Pages */}
        <div className="card overflow-hidden">
          <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-3 sm:mb-4 truncate">
            Top Pages
          </h3>
          {loading ? (
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                <div
                  key={i}
                  className="h-10 sm:h-12 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded"
                />
              ))}
            </div>
          ) : stats?.topPages && stats.topPages.length > 0 ? (
            <div className="space-y-1 sm:space-y-2 max-h-96 overflow-y-auto">
              {stats.topPages.slice(0, 15).map((page, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-2 p-2 sm:p-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors min-w-0"
                >
                  <span className="text-xs sm:text-sm text-secondary-700 dark:text-secondary-300 truncate flex-1 min-w-0">
                    {index + 1}. {page.page}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-primary-600 dark:text-primary-400 shrink-0">
                    {formatNumber(page.views)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-secondary-500 text-center py-6 sm:py-8">
              No data available
            </p>
          )}
        </div>

        {/* Top Sources */}
        <div className="card overflow-hidden">
          <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-3 sm:mb-4 truncate">
            Top Traffic Sources
          </h3>
          {loading ? (
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                <div
                  key={i}
                  className="h-10 sm:h-12 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded"
                />
              ))}
            </div>
          ) : stats?.topSources && stats.topSources.length > 0 ? (
            <div className="space-y-1 sm:space-y-2 max-h-96 overflow-y-auto">
              {stats.topSources.slice(0, 15).map((source, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-2 p-2 sm:p-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors min-w-0"
                >
                  <span className="text-xs sm:text-sm text-secondary-700 dark:text-secondary-300 capitalize truncate flex-1 min-w-0">
                    {index + 1}. {source.source || 'Direct'}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-green-600 dark:text-green-400 shrink-0">
                    {formatNumber(source.sessions)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-secondary-500 text-center py-6 sm:py-8">
              No data available
            </p>
          )}
        </div>

        {/* Device Breakdown */}
        <div className="card overflow-hidden">
          <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-3 sm:mb-4 truncate">
            Device Breakdown
          </h3>
          {loading ? (
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className="h-14 sm:h-16 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded"
                />
              ))}
            </div>
          ) : stats?.deviceBreakdown && stats.deviceBreakdown.length > 0 ? (
            <div className="space-y-2 sm:space-y-3">
              {stats.deviceBreakdown.map((device, index) => {
                const sessions = device.sessions || device.count || 0;
                const percentage =
                  (stats?.totalSessions || 0) > 0
                    ? (sessions / (stats?.totalSessions || 1)) * 100
                    : device.percentage || 0;

                return (
                  <div key={index} className="space-y-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm font-medium text-secondary-700 dark:text-secondary-300 capitalize truncate flex-1 min-w-0">
                        {device.device}
                      </span>
                      <span className="text-xs sm:text-sm font-semibold text-secondary-900 dark:text-secondary-100 shrink-0">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-2 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          index === 0
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600'
                            : index === 1
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-orange-500 to-orange-600'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-secondary-500 truncate">
                      {formatNumber(sessions)} sessions
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-secondary-500 text-center py-6 sm:py-8">
              No data available
            </p>
          )}
        </div>
      </div>

      {/* Browser Breakdown & Email Campaign Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Browser Breakdown */}
        <div className="card overflow-hidden">
          <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-3 sm:mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Browser Breakdown
          </h3>
          {loading ? (
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className="h-14 sm:h-16 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded"
                />
              ))}
            </div>
          ) : stats?.browserBreakdown && stats.browserBreakdown.length > 0 ? (
            <div className="space-y-2 sm:space-y-3">
              {stats.browserBreakdown.map((browser, index) => {
                const sessions = browser.sessions || browser.count || 0;
                const totalBrowserSessions = (stats?.browserBreakdown || []).reduce(
                  (sum, b) => sum + (b.sessions || b.count || 0),
                  0
                );
                const percentage =
                  totalBrowserSessions > 0
                    ? (sessions / totalBrowserSessions) * 100
                    : browser.percentage || 0;

                return (
                  <div key={index} className="space-y-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm font-medium text-secondary-700 dark:text-secondary-300 truncate flex-1 min-w-0">
                        {browser.browser}
                      </span>
                      <span className="text-xs sm:text-sm font-semibold text-secondary-900 dark:text-secondary-100 shrink-0">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-2 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          index === 0
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600'
                            : index === 1
                            ? 'bg-gradient-to-r from-indigo-500 to-indigo-600'
                            : index === 2
                            ? 'bg-gradient-to-r from-pink-500 to-pink-600'
                            : 'bg-gradient-to-r from-cyan-500 to-cyan-600'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-secondary-500 truncate">
                      {formatNumber(sessions)} sessions
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-secondary-500 text-center py-6 sm:py-8">
              No browser data available
            </p>
          )}
        </div>

        {/* Email Campaign Summary */}
        <div className="card overflow-hidden">
          <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-3 sm:mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Campaign Summary
          </h3>
          {loading ? (
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className="h-10 sm:h-12 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded"
                />
              ))}
            </div>
          ) : stats?.emailCampaignSummary && stats.emailCampaignSummary.totalCampaigns > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Campaigns</p>
                  <p className="text-xl font-bold text-secondary-900 dark:text-secondary-50">
                    {stats?.emailCampaignSummary?.totalCampaigns || 0}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Sent</p>
                  <p className="text-xl font-bold text-secondary-900 dark:text-secondary-50">
                    {formatNumber(stats?.emailCampaignSummary?.totalSent || 0)}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-blue-500" />
                    <span className="text-xs sm:text-sm text-secondary-700 dark:text-secondary-300">
                      Open Rate
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                    {(stats?.emailCampaignSummary?.openRate || 0).toFixed(1)}%
                  </span>
                </div>

                <div className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
                  <div className="flex items-center gap-2">
                    <MousePointer className="w-4 h-4 text-green-500" />
                    <span className="text-xs sm:text-sm text-secondary-700 dark:text-secondary-300">
                      Click Rate
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                    {(stats?.emailCampaignSummary?.clickRate || 0).toFixed(1)}%
                  </span>
                </div>

                <div className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-500" />
                    <span className="text-xs sm:text-sm text-secondary-700 dark:text-secondary-300">
                      Delivery Rate
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                    {(stats?.emailCampaignSummary?.deliveryRate || 0).toFixed(1)}%
                  </span>
                </div>

                <div className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-xs sm:text-sm text-secondary-700 dark:text-secondary-300">
                      Bounce Rate
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                    {(stats?.emailCampaignSummary?.bounceRate || 0).toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Top Email Campaigns */}
              {stats.topEmailCampaigns && stats.topEmailCampaigns.length > 0 && (
                <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <h4 className="text-xs sm:text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                    Top Campaigns
                  </h4>
                  <div className="space-y-2 min-h-[300px] max-h-96 overflow-y-auto">
                    {(stats?.topEmailCampaigns || []).map((campaign, index) => (
                      <div
                        key={campaign?.campaignId || index}
                        className="p-2 rounded-lg bg-secondary-50 dark:bg-secondary-800 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-xs font-medium text-secondary-900 dark:text-secondary-50 flex-1 min-w-0 truncate">
                            {index + 1}. {campaign?.campaignName || 'Unknown Campaign'}
                          </span>
                          <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 shrink-0">
                            {campaign?.engagementScore || 0}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-secondary-600 dark:text-secondary-400">
                          <span>Open: {(campaign?.openRate || 0).toFixed(1)}%</span>
                          <span>Click: {(campaign?.clickRate || 0).toFixed(1)}%</span>
                          <span className="ml-auto">Sent: {formatNumber(campaign?.sent || 0)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-secondary-500 text-center py-6 sm:py-8">
              No email campaign data available
            </p>
          )}
        </div>
      </div>

      {/* Quick Actions Panel */}
      {!loading && !needsSelection && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/analytics/funnel')}
            className="card card-hover text-left p-4 group"
          >
            <div className="flex items-center justify-between mb-2">
              <Navigation className="w-8 h-8 text-primary-600 group-hover:scale-110 transition-transform" />
              <ArrowRight className="w-5 h-5 text-secondary-400 group-hover:text-primary-600 transition-colors" />
            </div>
            <h4 className="font-semibold text-secondary-900 dark:text-secondary-50">
              Funnel Analysis
            </h4>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
              View complete user journeys
            </p>
          </button>

          <button
            onClick={() => navigate('/email-campaigns')}
            className="card card-hover text-left p-4 group"
          >
            <div className="flex items-center justify-between mb-2">
              <Mail className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
              <ArrowRight className="w-5 h-5 text-secondary-400 group-hover:text-green-600 transition-colors" />
            </div>
            <h4 className="font-semibold text-secondary-900 dark:text-secondary-50">
              Email Campaigns
            </h4>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
              Track campaign performance
            </p>
          </button>

          <button
            onClick={() => navigate('/sessions')}
            className="card card-hover text-left p-4 group"
          >
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-purple-600 group-hover:scale-110 transition-transform" />
              <ArrowRight className="w-5 h-5 text-secondary-400 group-hover:text-purple-600 transition-colors" />
            </div>
            <h4 className="font-semibold text-secondary-900 dark:text-secondary-50">
              User Sessions
            </h4>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
              Analyze user behavior
            </p>
          </button>

          <button
            onClick={() => navigate('/email-logs')}
            className="card card-hover text-left p-4 group"
          >
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-8 h-8 text-orange-600 group-hover:scale-110 transition-transform" />
              <ArrowRight className="w-5 h-5 text-secondary-400 group-hover:text-orange-600 transition-colors" />
            </div>
            <h4 className="font-semibold text-secondary-900 dark:text-secondary-50">Email Logs</h4>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
              View email activity
            </p>
          </button>
        </div>
      )}

      {/* User Journey Insights & Recent Email Activity */}
      {!loading && !needsSelection && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* User Journey Insights */}
          <div className="card overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-primary-600" />
                Top User Journeys
              </h3>
              <button
                onClick={() => navigate('/analytics/funnel')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                View All <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            {!funnelData || !funnelData.sankey ? (
              <p className="text-xs sm:text-sm text-secondary-500 text-center py-8">
                No funnel data available
              </p>
            ) : (
              <div className="space-y-3">
                {/* Top conversion paths */}
                {funnelData.sankey.links?.slice(0, 5).map((link: any, index: number) => {
                  const sourceName =
                    funnelData.sankey.nodes.find((n: any) => n.id === link.source)?.name ||
                    link.source;
                  const targetName =
                    funnelData.sankey.nodes.find((n: any) => n.id === link.target)?.name ||
                    link.target;
                  const sessions = link.sessions?.length || link.value || 0;

                  return (
                    <div
                      key={index}
                      className="p-3 rounded-lg bg-secondary-50 dark:bg-secondary-800 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-mono bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded">
                          #{index + 1}
                        </span>
                        <span className="flex-1 text-xs font-semibold text-secondary-900 dark:text-secondary-100">
                          {sessions.toLocaleString()} sessions
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span
                          className="text-secondary-700 dark:text-secondary-300 truncate max-w-[40%]"
                          title={sourceName}
                        >
                          {sourceName}
                        </span>
                        <ArrowRight className="w-3 h-3 text-primary-500 flex-shrink-0" />
                        <span
                          className="text-secondary-700 dark:text-secondary-300 truncate max-w-[40%]"
                          title={targetName}
                        >
                          {targetName}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Summary stats */}
                <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700 grid grid-cols-2 gap-3">
                  <div className="text-center p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">
                      Total Paths
                    </p>
                    <p className="text-lg font-bold text-secondary-900 dark:text-secondary-50">
                      {funnelData.sankey.links?.length || 0}
                    </p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">
                      Unique Pages
                    </p>
                    <p className="text-lg font-bold text-secondary-900 dark:text-secondary-50">
                      {funnelData.sankey.nodes?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Email Activity */}
          <div className="card overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 dark:text-secondary-50 flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-600" />
                Recent Email Activity
              </h3>
              <button
                onClick={() => navigate('/email-logs')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                View All <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            {emailLogsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div
                    key={i}
                    className="h-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded"
                  />
                ))}
              </div>
            ) : emailLogsError ? (
              <div className="text-center py-8">
                <p className="text-xs sm:text-sm text-orange-500 mb-2">
                  ⚠️ Unable to load email logs
                </p>
                <p className="text-xs text-secondary-500">
                  {emailLogsError?.message ||
                    'Query timed out - please use the Email Logs page for detailed view'}
                </p>
              </div>
            ) : !recentEmailLogs ||
              !recentEmailLogs.emailLogs ||
              recentEmailLogs.emailLogs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-xs sm:text-sm text-secondary-500 mb-2">
                  No recent email activity
                </p>
                <p className="text-xs text-secondary-400">
                  Email logs will appear here once campaigns are sent
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(() => {
                  // Client-side deduplication: Keep only unique messageId + eventType combinations
                  const seen = new Set<string>();
                  const uniqueLogs = recentEmailLogs.emailLogs.filter((log: any) => {
                    const key = `${log.messageId}-${log.eventType}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });

                  return uniqueLogs.map((log: any, index: number) => {
                    const getEventIcon = (event: string) => {
                      if (!event) return <Mail className="w-4 h-4 text-secondary-500" />;
                      switch (event.toLowerCase()) {
                        case 'send':
                          return <Send className="w-4 h-4 text-blue-500" />;
                        case 'open':
                          return <MailOpen className="w-4 h-4 text-green-500" />;
                        case 'click':
                          return <MousePointerClick className="w-4 h-4 text-purple-500" />;
                        default:
                          return <Mail className="w-4 h-4 text-secondary-500" />;
                      }
                    };

                    const getEventColor = (event: string) => {
                      if (!event)
                        return 'bg-secondary-50 dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700';
                      switch (event.toLowerCase()) {
                        case 'send':
                          return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
                        case 'open':
                          return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
                        case 'click':
                          return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
                        default:
                          return 'bg-secondary-50 dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700';
                      }
                    };

                    return (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border transition-colors ${getEventColor(
                          log.eventType || log.event
                        )}`}
                      >
                        <div className="flex items-start gap-3">
                          {getEventIcon(log.eventType || log.event)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-semibold text-secondary-900 dark:text-secondary-100 capitalize">
                                {log.eventType || log.event || 'Unknown'}
                              </span>
                              <span className="text-xs text-secondary-500">•</span>
                              <span className="text-xs text-secondary-600 dark:text-secondary-400">
                                {log.timestamp
                                  ? new Date(log.timestamp).toLocaleTimeString()
                                  : 'N/A'}
                              </span>
                            </div>
                            <p className="text-xs text-secondary-700 dark:text-secondary-300 truncate">
                              {log.subject || 'No subject'}
                            </p>
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 truncate mt-1">
                              To: {log.to || log.recipient || 'Unknown'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
